/**
 * Utility functions for detecting the user's country.
 */

// In-memory cache (lasts until the user manually refreshes the page or closes the tab)
let in_memory_country: string | null = null;

/**
 * Get the user's country code with sequential service calls and a timezone fallback.
 * Behavior:
 * - Persists across internal navigation within the same tab via sessionStorage.
 * - Re-detects on manual refresh (reload) by clearing the session value.
 * - Does NOT use localStorage at all.
 * @returns {Promise<string | null>} Uppercased country code or null if detection fails.
 */
export const get_user_country = async (): Promise<string | null> => {
  // SSR guard
  if (typeof window === "undefined") return null;

  // 1) Session-based persistence (across pages in the same tab) and manual reload handling
  try {
    const navEntry = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;

    const isReload = navEntry
      ? navEntry.type === "reload"
      : (performance as any).navigation?.type === 1; // legacy fallback: 1 = reload

    console.log("isReload", isReload);
    console.log("navEntry", navEntry);

    if (isReload) {
      sessionStorage.removeItem("user_country_session");
    } else {
      const session_country = sessionStorage.getItem("user_country_session");
      if (session_country) {
        in_memory_country = session_country.toUpperCase();
        return in_memory_country;
      }
    }
  } catch (error) {
    // Ignore session/navigation detection issues silently
    console.log("Error in get_user_country:", error);
  }

  // 2) If already cached in memory (same page without reload), return it
  if (in_memory_country) return in_memory_country;

  // 3) Try real-time detection via external services (sequential)
  const services = [
    "https://ipapi.co/json/",
    "https://api.country.is/",
    "https://ipinfo.io/json",
  ];

  for (const service_url of services) {
    try {
      const controller = new AbortController();
      const timeout_id = setTimeout(() => controller.abort(), 3000);

      const response = await fetch(service_url, { signal: controller.signal });
      clearTimeout(timeout_id);

      if (!response.ok) continue;

      const data = await response.json();
      let country_code: string | null = null;

      if (data.country_code) country_code = data.country_code;
      else if (data.country) country_code = data.country;
      else if (data.countryCode) country_code = data.countryCode;

      if (country_code) {
        const normalized_country = country_code.toUpperCase();
        in_memory_country = normalized_country;
        try {
          sessionStorage.setItem("user_country_session", normalized_country);
        } catch {
          // Ignore sessionStorage write errors
        }
        return normalized_country;
      }
    } catch {
      // Ignore network/service errors and continue to the next service
    }
  }

  // 4) Final fallback: map by browser timezone
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezone_to_country = {
      "America/New_York": "US",
      "America/Los_Angeles": "US",
      "America/Chicago": "US",
      "America/Denver": "US",
      "Europe/London": "GB",
      "Europe/Paris": "FR",
      "Europe/Berlin": "DE",
      "Europe/Madrid": "ES",
      "Europe/Rome": "IT",
      "Asia/Tokyo": "JP",
      "Asia/Shanghai": "CN",
      "Asia/Kolkata": "IN",
      "Australia/Sydney": "AU",
      "America/Sao_Paulo": "BR",
      "America/Mexico_City": "MX",
      "Africa/Lagos": "NG",
      "Africa/Cairo": "EG",
    } as const;

    const mapped_country =
      timezone_to_country[timezone as keyof typeof timezone_to_country];
    if (mapped_country) {
      const normalized_country = mapped_country.toUpperCase();
      in_memory_country = normalized_country;
      try {
        sessionStorage.setItem("user_country_session", normalized_country);
      } catch {
        // Ignore sessionStorage write errors
      }
      return normalized_country;
    }
  } catch {
    // Ignore timezone detection errors
  }

  return null;
};

/**
 * Cached-only helpers
 * Use these when you want to read or update the country value without triggering detection
 * (no network calls, no timezone mapping).
 */

/**
 * Returns the cached country code if available (in-memory or sessionStorage), otherwise null.
 * Does not perform any detection logic.
 */
export const get_user_country_or_detect = async (): Promise<string | null> => {
  // SSR guard
  if (typeof window === "undefined") return null;

  // 1) Return in-memory cache if present (fast path)
  if (in_memory_country) return in_memory_country;

  // 2) Try sessionStorage cache
  try {
    const val = sessionStorage.getItem("user_country_session");
    if (val) return val.toUpperCase();
  } catch {
    // ignore sessionStorage read errors
  }

  // 3) Fallback to full detection when no cached value is available
  // get_user_country will seed both in-memory and sessionStorage caches
  return await get_user_country();
};

/**
 * Sets the cached country (in-memory and sessionStorage).
 * Useful when you already know the country (e.g., user selection) and want to persist it.
 */
export const set_user_country_cached = (code: string | null): void => {
  if (typeof window === "undefined") return;
  if (!code) {
    in_memory_country = null;
    try {
      sessionStorage.removeItem("user_country_session");
    } catch {
      // ignore
    }
    return;
  }
  const normalized = code.toUpperCase();
  in_memory_country = normalized;
  try {
    sessionStorage.setItem("user_country_session", normalized);
  } catch {
    // ignore
  }
};

/**
 * Clears the cached country from memory and sessionStorage.
 */
export const clear_user_country_cached = (): void => {
  if (typeof window === "undefined") return;
  in_memory_country = null;
  try {
    sessionStorage.removeItem("user_country_session");
  } catch {
    // ignore
  }
};
